import * as echarts from 'echarts/core';
import { getCityNameByCode, getCityInfoByCode } from '../../../../../utils/cityUtils';
import { getCityGeoCoord } from '../../../../../utils/cityGeoCoords';
import { getPrimaryPhaseRowMetricValue } from './orderPrimaryPhaseMetric';

/** 业务省份简称 → ECharts/DataV 中国地图区域名 */
export const PROVINCE_TO_MAP_NAME = {
	北京: '北京市',
	天津: '天津市',
	上海: '上海市',
	重庆: '重庆市',
	河北: '河北省',
	山西: '山西省',
	辽宁: '辽宁省',
	吉林: '吉林省',
	黑龙江: '黑龙江省',
	江苏: '江苏省',
	浙江: '浙江省',
	安徽: '安徽省',
	福建: '福建省',
	江西: '江西省',
	山东: '山东省',
	河南: '河南省',
	湖北: '湖北省',
	湖南: '湖南省',
	广东: '广东省',
	海南: '海南省',
	四川: '四川省',
	贵州: '贵州省',
	云南: '云南省',
	陕西: '陕西省',
	甘肃: '甘肃省',
	青海: '青海省',
	台湾: '台湾省',
	内蒙古: '内蒙古自治区',
	广西: '广西壮族自治区',
	西藏: '西藏自治区',
	宁夏: '宁夏回族自治区',
	新疆: '新疆维吾尔自治区',
	香港: '香港特别行政区',
	澳门: '澳门特别行政区',
};

/** DataV 中国地图省级区域名（与 geoJson properties.name 对齐） */
export const CHINA_PROVINCE_MAP_NAMES = [...new Set(Object.values(PROVINCE_TO_MAP_NAME))];

export const normalizeProvinceMapName = (provinceName) => {
	if (!provinceName) return '';
	const trimmed = String(provinceName).trim();
	if (PROVINCE_TO_MAP_NAME[trimmed]) return PROVINCE_TO_MAP_NAME[trimmed];
	if (trimmed.endsWith('省') || trimmed.endsWith('市') || trimmed.includes('自治区') || trimmed.includes('特别行政区')) {
		return trimmed;
	}
	return `${trimmed}省`;
};

export const resolveProvinceMapNameFromCityCode = (cityCode) => {
	const code = String(cityCode ?? '').trim();
	if (!/^\d{6}$/.test(code)) return '';
	const cityInfo = getCityInfoByCode(code);
	return normalizeProvinceMapName(cityInfo?.provinceName);
};

/** 客户筛选 → 需要高亮的省级区域（北京市、安徽省等） */
export const resolveHighlightProvincesFromCustomerFilter = (customerIds = [], { customers = [], orders = [] } = {}) => {
	const idSet = new Set((customerIds || []).map((id) => String(id)).filter(Boolean));
	if (!idSet.size) return [];

	const names = new Set();

	for (const customer of customers) {
		if (!idSet.has(String(customer?._id))) continue;
		const provinceName = resolveProvinceMapNameFromCityCode(customer?.city);
		if (provinceName) names.add(provinceName);
	}

	for (const order of orders) {
		if (!idSet.has(String(order?.customer))) continue;
		const cityCode = resolveOrderCityCode(order);
		if (!cityCode) continue;
		const cityInfo = getCityInfoByCode(cityCode);
		const provinceName = normalizeProvinceMapName(cityInfo?.provinceName);
		if (provinceName) names.add(provinceName);
	}

	return [...names].sort((a, b) => a.localeCompare(b, 'zh-CN'));
};

/** 已选客户所在城市（用于地图聚焦，无订单时也可居中） */
export const buildCustomerFilterCityRows = (customerIds = [], { customers = [], orders = [] } = {}) => {
	const idSet = new Set((customerIds || []).map((id) => String(id)).filter(Boolean));
	if (!idSet.size) return [];

	const rows = [];
	const seenCodes = new Set();

	const appendCityRow = (cityCode) => {
		const code = String(cityCode ?? '').trim();
		if (!/^\d{6}$/.test(code) || seenCodes.has(code)) return;
		const coord = getCityGeoCoord(code);
		const cityInfo = getCityInfoByCode(code);
		if (!coord || !cityInfo) return;
		seenCodes.add(code);
		rows.push({
			cityCode: code,
			cityName: cityInfo.name || getCityNameByCode(code) || code,
			provinceMapName: normalizeProvinceMapName(cityInfo.provinceName),
			coord,
			count: 0,
			totalPrice: 0,
		});
	};

	for (const customer of customers) {
		if (!idSet.has(String(customer?._id))) continue;
		appendCityRow(customer?.city);
	}

	for (const order of orders) {
		if (!idSet.has(String(order?.customer))) continue;
		appendCityRow(resolveOrderCityCode(order));
	}

	return rows;
};

export const buildChinaMapProvinceRows = (provinceRows = [], highlightProvinceNames = []) => {
	const highlightSet = new Set(highlightProvinceNames.filter(Boolean));
	if (!highlightSet.size) return provinceRows;

	const byName = new Map(provinceRows.map((row) => [row.name, row]));
	return [...highlightSet].map((name) => byName.get(name) ?? { name, count: 0, totalPrice: 0 });
};

const mergeProvinceRowsWithHighlights = buildChinaMapProvinceRows;

/**
 * 地图省级 series：有订单走 visualMap；客户筛选命中省份即使无订单也写入并高亮。
 */
/** 自制图省份维度：按所选指标映射 visualMap 区间 */
export const buildChinaMapMetricVisualRange = (provinceRows = [], metric) => {
	if (!provinceRows.length) {
		return { min: 1, max: 2, useRank: false };
	}

	const values = provinceRows.map((row) => getPrimaryPhaseRowMetricValue(row, metric));
	const minValue = Math.min(...values);
	const maxValue = Math.max(...values);

	if (maxValue === 0 && provinceRows.length > 1) {
		return { min: 1, max: provinceRows.length, useRank: true };
	}

	if (maxValue === 0) {
		return { min: 1, max: 2, useRank: false };
	}

	if (minValue === maxValue && provinceRows.length > 1) {
		return { min: 1, max: provinceRows.length, useRank: true };
	}

	return { min: minValue, max: maxValue, useRank: false };
};

/** 自制图省份维度：按指标值为省级 choropleth 上色 */
export const buildChinaMapMetricSeriesData = (provinceRows = [], metric, { useRank = false } = {}) => {
	const rows = useRank ? [...provinceRows].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN')) : provinceRows;

	return rows.map((row, index) => ({
		name: row.name,
		value: useRank ? index + 1 : getPrimaryPhaseRowMetricValue(row, metric),
		row,
	}));
};

export const buildChinaMapSeriesData = (provinceRows = [], { useRank = false, highlightProvinceNames = [] } = {}) => {
	const mergedRows = mergeProvinceRowsWithHighlights(provinceRows, highlightProvinceNames);
	if (!mergedRows.length) return [];

	const rows = useRank ? [...mergedRows].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN')) : mergedRows;
	const highlightSet = new Set(highlightProvinceNames.filter(Boolean));

	return rows.map((row, index) => {
		const metricValue = useRank ? index + 1 : row.count > 0 ? row.count : highlightSet.has(row.name) ? 1 : 0;
		return {
			name: row.name,
			value: metricValue,
			row,
			isHighlight: highlightSet.has(row.name),
		};
	});
};

/** ECharts 地图区域点击：仅 map series 上报，避免 geo 与 map 同点触发两次 */
export const isChinaMapRegionClick = (params) => {
	if (!params?.name) return false;
	if (params.seriesType === 'scatter') return false;
	return params.seriesType === 'map';
};

/** 地图槽位：始终用明确 rem 高度，避免 ECharts canvas 与容器错位 */
export const resolveMapSlotStyle = (height = '10rem', { fillSlot = false } = {}) =>
	fillSlot
		? { width: '100%', height: '100%', minHeight: 0 }
		: { width: '100%', height, minHeight: height };

/** 经营看板首行：宽扁槽位下让中国地图尽量铺满宽度 */
export const resolveWideInlineGeoLayout = (width = 0, height = 0) => {
	if (!width || !height) {
		return { layoutCenter: ['50%', '45%'], layoutSize: '118%', aspectScale: 0.74 };
	}

	const ratio = width / height;
	if (ratio <= 1.1) return {};

	return {
		layoutCenter: ['50%', ratio > 1.85 ? '43%' : '45%'],
		layoutSize: `${Math.min(140, Math.round(104 + (ratio - 1) * 16))}%`,
		aspectScale: Math.max(0.66, 0.8 - (ratio - 1) * 0.05),
	};
};

/** 紧凑地图 visualMap：固定左下竖条，避免横向色条在矮容器里文字错位 */
export const resolveInlineVisualMapLayout = (compact = false, chartHeight = 0) => {
	const itemHeight = compact
		? chartHeight > 0
			? Math.min(48, Math.max(30, Math.round(chartHeight * 0.45)))
			: 40
		: 72;

	return {
		orient: 'vertical',
		left: compact ? 4 : 12,
		bottom: compact ? 4 : 12,
		itemWidth: compact ? 8 : 10,
		itemHeight,
		textGap: 2,
		align: 'left',
	};
};

/** 仅使用客户主数据维护的城市代码 */
export const resolveOrderCityCode = (order) => {
	const code = String(order?.customerCity ?? '').trim();
	return /^\d{6}$/.test(code) ? code : '';
};

export const buildOrderCityBuckets = (orders = []) => {
	const buckets = new Map();
	let unknownCount = 0;

	for (const order of orders) {
		const cityCode = resolveOrderCityCode(order);
		if (!cityCode) {
			unknownCount += 1;
			continue;
		}

		const coord = getCityGeoCoord(cityCode);
		const cityInfo = getCityInfoByCode(cityCode);
		if (!coord || !cityInfo) {
			unknownCount += 1;
			continue;
		}

		const prev = buckets.get(cityCode) ?? {
			cityCode,
			cityName: cityInfo.name || getCityNameByCode(cityCode) || cityCode,
			provinceMapName: normalizeProvinceMapName(cityInfo.provinceName),
			coord,
			count: 0,
			totalPrice: 0,
		};

		buckets.set(cityCode, {
			...prev,
			count: prev.count + 1,
			totalPrice: prev.totalPrice + (Number(order.totalPrice) || 0),
		});
	}

	const rows = [...buckets.values()].sort((a, b) => b.count - a.count);
	const provinceRows = buildProvinceRowsFromCities(rows);

	return { rows, provinceRows, unknownCount };
};

const buildProvinceRowsFromCities = (cityRows) => {
	const buckets = new Map();
	for (const city of cityRows) {
		if (!city.provinceMapName) continue;
		const prev = buckets.get(city.provinceMapName) ?? { count: 0, totalPrice: 0 };
		buckets.set(city.provinceMapName, {
			count: prev.count + city.count,
			totalPrice: prev.totalPrice + city.totalPrice,
		});
	}
	return [...buckets.entries()]
		.map(([name, stats]) => ({ name, ...stats }))
		.sort((a, b) => b.count - a.count);
};

/** API chartAggregates.geo → 与 buildOrderCityBuckets 相同结构（补全坐标与省聚合） */
export const enrichGeoBucketsFromApi = (geo = null) => {
	if (!geo) {
		return { rows: [], provinceRows: [], unknownCount: 0 };
	}

	const buckets = new Map();
	let unknownCount = Number(geo.unknownCount) || 0;

	for (const item of geo.cityBuckets ?? []) {
		const cityCode = String(item?.cityCode ?? '').trim();
		if (!/^\d{6}$/.test(cityCode)) {
			unknownCount += Number(item?.count) || 0;
			continue;
		}

		const coord = getCityGeoCoord(cityCode);
		const cityInfo = getCityInfoByCode(cityCode);
		if (!coord || !cityInfo) {
			unknownCount += Number(item?.count) || 0;
			continue;
		}

		buckets.set(cityCode, {
			cityCode,
			cityName: cityInfo.name || getCityNameByCode(cityCode) || cityCode,
			provinceMapName: normalizeProvinceMapName(cityInfo.provinceName),
			coord,
			count: Number(item.count) || 0,
			totalPrice: Number(item.totalPrice) || 0,
		});
	}

	const rows = [...buckets.values()].sort((a, b) => b.count - a.count);
	const provinceRows =
		Array.isArray(geo.provinceRows) && geo.provinceRows.length > 0 ? geo.provinceRows : buildProvinceRowsFromCities(rows);

	return { rows, provinceRows, unknownCount };
};

const DEFAULT_MAP_ZOOM = 1.12;
const CHINA_LNG_BOUNDS = [73, 135];
const CHINA_LAT_BOUNDS = [18, 54];
const REF_LNG_SPAN = 56;
const REF_LAT_SPAN = 36;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const readCoordBounds = (coords = []) => {
	let minLng = Infinity;
	let maxLng = -Infinity;
	let minLat = Infinity;
	let maxLat = -Infinity;

	for (const [lng, lat] of coords) {
		minLng = Math.min(minLng, lng);
		maxLng = Math.max(maxLng, lng);
		minLat = Math.min(minLat, lat);
		maxLat = Math.max(maxLat, lat);
	}

	return {
		minLng,
		maxLng,
		minLat,
		maxLat,
		lngSpan: maxLng - minLng,
		latSpan: maxLat - minLat,
		center: [
			Number(clamp((minLng + maxLng) / 2, CHINA_LNG_BOUNDS[0], CHINA_LNG_BOUNDS[1]).toFixed(4)),
			Number(clamp((minLat + maxLat) / 2, CHINA_LAT_BOUNDS[0], CHINA_LAT_BOUNDS[1]).toFixed(4)),
		],
	};
};

/** 按省聚合中心点，避免同省多城把视野拉得过大 */
const buildProvinceCentroidCoords = (cityRows = []) => {
	const buckets = new Map();

	for (const row of cityRows) {
		if (!row?.provinceMapName || !Array.isArray(row.coord) || row.coord.length < 2) continue;
		const prev = buckets.get(row.provinceMapName) ?? { lngSum: 0, latSum: 0, weight: 0 };
		const weight = Math.max(1, Number(row.count) || 1);
		buckets.set(row.provinceMapName, {
			lngSum: prev.lngSum + row.coord[0] * weight,
			latSum: prev.latSum + row.coord[1] * weight,
			weight: prev.weight + weight,
		});
	}

	return [...buckets.values()].map(({ lngSum, latSum, weight }) => [lngSum / weight, latSum / weight]);
};

/** 客户分布：始终居中；区域集中时自动放大，全国分散时保持概览缩放 */
export const computeCustomerGeoViewport = (cityRows = [], { compact = false } = {}) => {
	const provinceCentroids = buildProvinceCentroidCoords(cityRows);
	const cityCoords = cityRows.map((row) => row.coord).filter((coord) => Array.isArray(coord) && coord.length >= 2);
	const viewportCoords = provinceCentroids.length > 0 ? provinceCentroids : cityCoords;

	if (viewportCoords.length === 0) {
		return { center: null, zoom: DEFAULT_MAP_ZOOM, maxRoamZoom: compact ? 4 : 3.5 };
	}

	const bounds = readCoordBounds(viewportCoords);
	const provinceCount = provinceCentroids.length || new Set(cityRows.map((row) => row.provinceMapName).filter(Boolean)).size;
	const isNationwide = provinceCount >= 10 || (bounds.lngSpan > 28 && bounds.latSpan > 18 && provinceCount >= 6);

	if (isNationwide) {
		return {
			center: bounds.center,
			zoom: DEFAULT_MAP_ZOOM,
			maxRoamZoom: compact ? 2.4 : 3.5,
		};
	}

	const isSingle = viewportCoords.length === 1;
	const pad = compact ? 1.22 : 1.38;
	const minLngSpan = isSingle ? (compact ? 4.5 : 6.5) : Math.max(bounds.lngSpan, compact ? 2.2 : 3.2);
	const minLatSpan = isSingle ? (compact ? 3.6 : 5) : Math.max(bounds.latSpan, compact ? 1.8 : 2.8);
	const effectiveLngSpan = minLngSpan * pad;
	const effectiveLatSpan = minLatSpan * pad;
	const rawZoom = Math.min(
		(DEFAULT_MAP_ZOOM * REF_LNG_SPAN) / effectiveLngSpan,
		(DEFAULT_MAP_ZOOM * REF_LAT_SPAN) / effectiveLatSpan,
	);
	const maxZoom = compact ? 3.45 : 2.85;
	const minZoom = compact ? 1.42 : 1.28;
	const zoom = Math.min(maxZoom, Math.max(minZoom, rawZoom));

	return {
		center: bounds.center,
		zoom: Number(zoom.toFixed(2)),
		maxRoamZoom: compact ? 4.2 : 3.5,
	};
};

/** 从已注册 china 地图读取省级几何中心（无城市坐标时的聚焦回退） */
export const resolveProvinceFeatureCenter = (provinceMapName = '') => {
	const normalized = normalizeProvinceMapName(provinceMapName);
	if (!normalized) return null;

	const geo = typeof echarts !== 'undefined' ? echarts.getMap('china') : null;
	const feature = geo?.geoJson?.features?.find((item) => item?.properties?.name === normalized);
	const center = feature?.properties?.cp ?? feature?.properties?.center;
	if (!Array.isArray(center) || center.length < 2) return null;

	return [Number(center[0]), Number(center[1])];
};

/** 点击省份后，按该省城市订单点聚焦放大 */
export const computeProvinceCityGeoViewport = (cityRows = [], provinceMapName = '', { compact = false } = {}) => {
	const provinceCities = cityRows.filter((row) => row.provinceMapName === provinceMapName);
	const coords = provinceCities.map((row) => row.coord).filter((coord) => Array.isArray(coord) && coord.length >= 2);

	if (!coords.length) {
		const fallbackCenter = resolveProvinceFeatureCenter(provinceMapName);
		if (fallbackCenter) {
			return {
				center: fallbackCenter,
				zoom: compact ? 2.85 : 2.35,
				maxRoamZoom: compact ? 6.5 : 5.8,
			};
		}
		return { center: null, zoom: DEFAULT_MAP_ZOOM, maxRoamZoom: compact ? 4.2 : 3.5 };
	}

	const bounds = readCoordBounds(coords);
	const isSingle = coords.length === 1;
	const pad = compact ? 1.12 : 1.22;
	const minLngSpan = isSingle ? (compact ? 0.75 : 1.1) : Math.max(bounds.lngSpan, compact ? 0.55 : 0.85);
	const minLatSpan = isSingle ? (compact ? 0.6 : 0.9) : Math.max(bounds.latSpan, compact ? 0.45 : 0.7);
	const effectiveLngSpan = minLngSpan * pad;
	const effectiveLatSpan = minLatSpan * pad;
	const rawZoom = Math.min(
		(DEFAULT_MAP_ZOOM * REF_LNG_SPAN) / effectiveLngSpan,
		(DEFAULT_MAP_ZOOM * REF_LAT_SPAN) / effectiveLatSpan,
	);
	const maxZoom = compact ? 7 : 5.8;
	const minZoom = compact ? 2.8 : 2.2;
	const zoom = Math.min(maxZoom, Math.max(minZoom, rawZoom));

	return {
		center: bounds.center,
		zoom: Number(zoom.toFixed(2)),
		maxRoamZoom: compact ? 8 : 6.5,
	};
};

export const filterCityRowsByProvince = (cityRows = [], provinceMapName = '') => {
	if (!provinceMapName) return cityRows;
	return cityRows.filter((row) => row.provinceMapName === provinceMapName);
};

/** 点击城市订单点后聚焦放大 */
export const computeSingleCityGeoViewport = (cityRow = null, { compact = false } = {}) => {
	if (!cityRow?.coord || cityRow.coord.length < 2) {
		return { center: null, zoom: DEFAULT_MAP_ZOOM, maxRoamZoom: compact ? 4.2 : 3.5 };
	}

	const bounds = readCoordBounds([cityRow.coord]);
	const pad = compact ? 1.08 : 1.12;
	const minLngSpan = compact ? 0.32 : 0.48;
	const minLatSpan = compact ? 0.26 : 0.38;
	const effectiveLngSpan = minLngSpan * pad;
	const effectiveLatSpan = minLatSpan * pad;
	const rawZoom = Math.min(
		(DEFAULT_MAP_ZOOM * REF_LNG_SPAN) / effectiveLngSpan,
		(DEFAULT_MAP_ZOOM * REF_LAT_SPAN) / effectiveLatSpan,
	);
	const maxZoom = compact ? 10 : 8.2;
	const minZoom = compact ? 4.8 : 3.8;
	const zoom = Math.min(maxZoom, Math.max(minZoom, rawZoom));

	return {
		center: bounds.center,
		zoom: Number(zoom.toFixed(2)),
		maxRoamZoom: compact ? 11 : 9,
	};
};

export const findCityRowByCode = (cityRows = [], cityCode = '') => {
	const code = String(cityCode ?? '').trim();
	if (!code) return null;
	return cityRows.find((row) => row.cityCode === code) ?? null;
};
